import React, { useState, useCallback, useMemo } from "react";
import Form from "react-bootstrap/Form";
import _ from "lodash";
import SearchBox from "../../common/components/search/searchBox";
import ReviewMarksTable from "./reviewMarksTable";
import { useTranslation } from "react-i18next";
import { MarkState } from "../../common/enumerations/enums";

const ReviewMarksTableHeader = ({
  marks: allMarks,
  handleApprove,
  handleReject,
  canApproveMarks,
}) => {
  const { t } = useTranslation(["marketdata"]);
  const [tableState, setTableState] = useState({
    pageSize: 20,
    searchQuery: "",
    submittedOnly: false,
    sortColumn: { path: "updatedDate", order: "desc" },
  });

  const handleSearch = useCallback((query) => {
    setTableState((prev) => ({ ...prev, searchQuery: query }));
  }, []);

  const handleSort = useCallback((sortColumn) => {
    setTableState((prev) => ({ ...prev, sortColumn }));
  }, []);

  const handleToggleSubmittedOnly = useCallback(() => {
    setTableState((prev) => ({
      ...prev,
      submittedOnly: !prev.submittedOnly,
    }));
  }, []);

  const filteredData = useMemo(() => {
    const { sortColumn, searchQuery, submittedOnly } = tableState;

    let filtered = [...allMarks];

    if (submittedOnly)
      filtered = filtered.filter((u) => u.markState === MarkState.Submitted);

    if (searchQuery)
      filtered = filtered.filter(
        (u) =>
          (u.organizationName != null &&
            u.organizationName
              .toLowerCase()
              .includes(searchQuery.toLowerCase())) ||
          (u.termName != null &&
            u.termName.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (u.priceBasisName != null &&
            u.priceBasisName
              .toLowerCase()
              .includes(searchQuery.toLowerCase())) ||
          (u.qualityAttributeValues.length > 0 &&
            u.qualityAttributeValues.filter((q) =>
              q.value.toLowerCase().includes(searchQuery.toLowerCase())
            ).length)
      );

    // Add the qualityAttributeValues so we can sort properly
    filtered = filtered.map((f) => {
      return {
        ...f,
        ...f.qualityAttributeValues.reduce((acc, attribute) => {
          acc[attribute.qualityAttributeId] = attribute.value;
          return acc;
        }, {}),
      };
    });

    const sortedMarks = _.orderBy(
      filtered,
      [sortColumn.path],
      [sortColumn.order]
    );

    return { totalCount: filtered.length, marks: sortedMarks };
  }, [allMarks, tableState]);

  const approveAll = useCallback(() => {
    const submittedMarks = filteredData.marks
      .filter((m) => m.markState === MarkState.Submitted)
      .map((m) => m.markId);

    if (submittedMarks.length > 0) {
      handleApprove(submittedMarks);
    }
  }, [filteredData.marks, handleApprove]);

  const rejectAll = useCallback(() => {
    const submittedMarks = filteredData.marks
      .filter((m) => m.markState === MarkState.Submitted)
      .map((m) => m.markId);

    if (submittedMarks.length > 0) {
      handleReject(submittedMarks);
    }
  }, [filteredData.marks, handleReject]);

  const { searchQuery, sortColumn, submittedOnly } = tableState;
  const { totalCount, marks } = filteredData;

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col">
          <SearchBox value={searchQuery} onChange={handleSearch} />
        </div>
        <div className="col">
          <Form.Switch
            className="switch"
            type="switch"
            label={t("SubmittedOnly")}
            checked={submittedOnly}
            value={submittedOnly}
            onChange={handleToggleSubmittedOnly}
          />
        </div>
        <div className="col" style={{ textAlign: "end" }}>
          {canApproveMarks && (
            <button
              onClick={rejectAll}
              className="btn btn-sm btn-danger float-sm-right"
            >
              {t("RejectAll")}
            </button>
          )}
          {canApproveMarks && (
            <button
              onClick={approveAll}
              className="btn btn-sm btn-success float-sm-right"
              style={{ marginLeft: "15px" }}
            >
              {t("ApproveAll")}
            </button>
          )}
        </div>
      </div>
      <p>{t("RetrievedMarks", { count: totalCount })}</p>
      <ReviewMarksTable
        marks={marks}
        handleApprove={handleApprove}
        handleReject={handleReject}
        sortColumn={sortColumn}
        onSort={handleSort}
        canApproveMarks={canApproveMarks}
      />
    </div>
  );
};

export default ReviewMarksTableHeader;
